import PropTypes from 'prop-types'
import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { createAccount as createAccountAction } from '../../store/userSlice'
import {
  trackAuthenticationError,
  trackAccountFlowDropOff,
} from '../../analytics/tracking'
import { getItem } from '../../storeManager'
import messages from './messages'
import Box from './Box'
import useTimeout from 'js/hooks/useTimeout'
import useLocale from 'js/hooks/useLocale'
import LoaderThreeDots from '../Animation/LoaderThreeDots'

function SignUpSetUp({ userData, onComplete, onEmailExists }) {
  const [isReady, setIsReady] = useState(false)
  const [success, setSuccess] = useState()
  const [error, setError] = useState()
  const createError = useSelector((state) => state.user.errors.create)

  const oauthToken = userData.oauth_token

  useTimeout(() => {
    setIsReady(true)
  }, 3000)
  const dispatch = useDispatch()
  const { language } = useLocale()
  const createAccount = useCallback(
    () =>
      dispatch(
        createAccountAction({
          profile: userData,
          language,
        }),
      )
        .then(() => {
          setSuccess(true)
        })
        .catch((error) => {
          trackAuthenticationError({
            errorType: 'account_creation',
            authMethod: userData.oauth_service || 'email',
            errorMessage: error?.message || 'Account creation failed',
            couponCode: getItem('coupon') || '',
            entryPoint: getItem('EntryPoint') || '',
            userAgent: navigator.userAgent,
          })
        }),
    [dispatch, language, userData],
  )

  useEffect(() => {
    createAccount()
  }, [createAccount])

  useEffect(() => {
    if (
      isReady &&
      createError &&
      createError.errorType === 'existing_user' &&
      oauthToken
    ) {
      setError(true)
    }
    if (isReady && createError && createError.errorType === 'existing_user') {
      return onEmailExists()
    }
    if (isReady && createError) {
      trackAccountFlowDropOff({
        step: 'account_creation',
        scenario: 'upgrade',
        couponCode: getItem('coupon') || '',
        entryPoint: getItem('EntryPoint') || '',
      })
      return setError(true)
    }
    if (isReady && success) return onComplete()
  }, [isReady, success, onComplete, createError, onEmailExists, oauthToken])

  return (
    <div className="o-width-limiter mt-12 flex w-full flex-col">
      <Box>
        {error ? (
          <div className="flex flex-col items-center justify-center">
            <img
              src={require('media/apple-sad.png')}
              width="100"
              className="mb-24"
            />
            <p className="text-17">
              {createError ? (
                <FormattedMessage id="errorEmailAlreadyConnected" />
              ) : (
                <FormattedMessage
                  id="Account.createError"
                  defaultMessage="Something went wrong when creating your account. Please try again or contact us if the problem persists."
                />
              )}
            </p>
          </div>
        ) : (
          <div className="c-signup-flow__setup flex items-center">
            <img src="https://www.datocms-assets.com/23496/1701610696-frame-7442.png" />
            <h3 className="quincy py-16 text-40">
              <FormattedMessage {...messages.gettingYouSetUp} />
            </h3>
            <LoaderThreeDots />
          </div>
        )}
      </Box>
    </div>
  )
}

SignUpSetUp.propTypes = {
  createAccount: PropTypes.func,
  locale: PropTypes.shape({
    language: PropTypes.string,
    country: PropTypes.string,
  }),
  onComplete: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
  onEmailExists: PropTypes.func,
}

export default SignUpSetUp
